<script setup lang="ts">
import { getImageUrl, TABBAR_REPEAT_CLICK_EVENT } from '@/utils'

/**
 * 自定义底部导航栏（pages.config.ts 中 tabBar.custom: true）。
 * 挂在 default layout，自行判断当前页是否 tab 页决定渲染，tab 页无需任何配合；
 * 组件自带文档流占位块，页面内容不会被固定栏遮挡。
 * 参考 v1 CustomTabbar 改造，设计见 docs/superpowers/specs/2026-07-02-custom-tabbar-design.md。
 * tab 列表与 pages.config.ts 的 tabBar.list 保持一致；暂为静态常量，需按身份动态时再抽 store。
 */
interface TabItem {
  pagePath: string
  text: string
  icon: string
  iconSelected: string
}

const TAB_LIST: TabItem[] = [
  {
    pagePath: '/pages/index/index',
    text: '首页',
    icon: getImageUrl('/tabbar/home.png'),
    iconSelected: getImageUrl('/tabbar/home-selected.png'),
  },
  {
    pagePath: '/pages/workbench/index',
    text: '工作台',
    icon: getImageUrl('/tabbar/workbench.png'),
    iconSelected: getImageUrl('/tabbar/workbench-selected.png'),
  },
  {
    pagePath: '/pages/my/index',
    text: '我的',
    icon: getImageUrl('/tabbar/my.png'),
    iconSelected: getImageUrl('/tabbar/my-selected.png'),
  },
]

// 每个 tab 页持有独立组件实例，selected 即实例所在页面；
// setup 时计算一次，onShow 再同步兜底（首次启动时页面栈时序保险）
const selectedIndex = ref(-1)

function syncSelected() {
  const pages = getCurrentPages()
  const route = pages[pages.length - 1]?.route
  selectedIndex.value = TAB_LIST.findIndex(item => item.pagePath === `/${route}`)
}

syncSelected()
onShow(syncSelected)

const isTabPage = computed(() => selectedIndex.value !== -1)

function tabIcon(item: TabItem, index: number) {
  if (index === selectedIndex.value)
    return item.iconSelected
  return item.icon
}

function tabTextClass(index: number) {
  if (index === selectedIndex.value)
    return 'text-[#456FEF] font-600'
  return 'text-[#666]'
}

function onTabClick(item: TabItem, index: number) {
  if (index === selectedIndex.value) {
    // 原型 3.4：重复点击当前 tab，页面自动滚动回顶部。
    // 首页/工作台的滚动在 z-paging 内部 scroll-view，pageScrollTo 对其无效，
    // 故广播事件让页面自行调 z-paging 回顶；「我的」等普通滚动页仍由 pageScrollTo 覆盖
    uni.$emit(TABBAR_REPEAT_CLICK_EVENT, item.pagePath)
    uni.pageScrollTo({ scrollTop: 0, duration: 300 })
    return
  }
  uni.switchTab({ url: item.pagePath })
}
</script>

<template>
  <view v-if="isTabPage">
    <!-- 文档流占位，与下方 fixed 栏等高 -->
    <view class="h-100rpx pb-safe" />
    <view class="custom-tabbar fixed bottom-0 left-0 z-100 w-full bg-white pb-safe">
      <view class="h-100rpx flex">
        <view
          v-for="(item, index) in TAB_LIST"
          :key="item.pagePath"
          class="flex flex-1 flex-col items-center justify-center gap-4rpx"
          @click="onTabClick(item, index)"
        >
          <image
            class="h-48rpx w-48rpx"
            :src="tabIcon(item, index)"
            mode="aspectFit"
          />
          <text class="text-20rpx" :class="tabTextClass(index)">
            {{ item.text }}
          </text>
        </view>
      </view>
    </view>
  </view>
</template>

<style lang="scss" scoped>
.custom-tabbar {
  border-top: 1rpx solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 -8rpx 24rpx rgba(31, 41, 55, 0.06);
}
</style>
